import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiTokenService, { ApiToken } from '../services/apiTokenService';
import CreateTokenDialog from '../components/common/CreateTokenDialog';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';
import { AlertDialog } from '../components/ui/radix/AlertDialog';

const formatDate = (s: string | null) => {
  if (!s) return 'Never';
  return new Date(s).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ApiTokensPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selected, setSelected] = useState<ApiToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: tokens = [], isLoading, refetch } = useQuery({
    queryKey: ['apiTokens'],
    queryFn: apiTokenService.listTokens,
  });

  const toggleMutation = useMutation({
    mutationFn: apiTokenService.toggleToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiTokens'] });
      setSuccess('Token status updated');
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to update token'),
  });

  const revokeMutation = useMutation({
    mutationFn: apiTokenService.revokeToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiTokens'] });
      setSuccess('Token revoked');
      setOpenDelete(false);
      setSelected(null);
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to revoke token'),
  });

  const handleDeleteConfirm = () => {
    if (selected) revokeMutation.mutate(selected.id);
  };

  const handleCreateSuccess = (message: string) => {
    setSuccess(message);
    setOpenCreate(false);
    refetch();
    setTimeout(() => setSuccess(null), 5000);
  };

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <PageTitle>API tokens</PageTitle>
          <PageSub>Use tokens to authenticate Almaship API requests.</PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="md" onClick={() => refetch()} disabled={isLoading} title="Refresh tokens">
            <Icon name="refresh" size={14} />
          </Button>
          <Button variant="blue" onClick={() => setOpenCreate(true)}>
            <Icon name="plus" size={14} />
            <span>Create token</span>
          </Button>
        </div>
      </PageHead>

      {error && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">
            <Icon name="alertCircle" size={16} />
            <span>{error}</span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto' }}
            >
              <Icon name="x" size={12} />
            </button>
          </Callout>
        </div>
      )}
      {success && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="ok">
            <Icon name="checkCircle" size={16} />
            <span>{success}</span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setSuccess(null)}
              style={{ marginInlineStart: 'auto' }}
            >
              <Icon name="x" size={12} />
            </button>
          </Callout>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Active tokens</CardTitle>
          <a
            href="https://almaship.co/api-docs"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13 }}
          >
            API docs <Icon name="external" size={11} />
          </a>
        </CardHeader>
        {isLoading ? (
          <div className="muted" style={{ padding: 48, textAlign: 'center' }}>
            Loading tokens…
          </div>
        ) : tokens.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Icon name="key" size={36} color="var(--ink-4)" />
            <h3 style={{ fontSize: 16, marginTop: 12 }}>No API tokens yet</h3>
            <p className="muted" style={{ fontSize: 13, marginTop: 6, marginBottom: 18 }}>
              Create your first API token to integrate with external applications.
            </p>
            <Button variant="blue" onClick={() => setOpenCreate(true)}>
              <Icon name="plus" size={14} />
              <span>Create token</span>
            </Button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Abilities</th>
                <th>Last used</th>
                <th>Expires</th>
                <th>Created</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr key={token.id}>
                  <td style={{ fontWeight: 500 }}>{token.name}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {token.abilities.map((ability, i) => (
                        <Badge key={i}>{ability}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {formatDate(token.last_used_at)}
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {token.expires_at ? formatDate(token.expires_at) : 'Never'}
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {formatDate(token.created_at)}
                  </td>
                  <td>
                    <label className="check" style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={token.is_active}
                        onChange={() => toggleMutation.mutate(token.id)}
                        disabled={toggleMutation.isPending}
                      />
                      <span style={{ fontSize: 13 }}>
                        {token.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </label>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setSelected(token);
                        setOpenDelete(true);
                      }}
                      disabled={revokeMutation.isPending}
                      aria-label="Revoke token"
                      title="Revoke token"
                    >
                      <Icon name="trash" size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <CreateTokenDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSuccess={handleCreateSuccess}
        onError={setError}
      />

      <AlertDialog
        open={openDelete}
        onOpenChange={setOpenDelete}
        title="Revoke API token"
        description={
          selected
            ? `Are you sure you want to revoke "${selected.name}"? This action cannot be undone and any applications using this token will lose access immediately.`
            : ''
        }
        confirmLabel={revokeMutation.isPending ? 'Revoking…' : 'Revoke token'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        destructive
      />
    </Page>
  );
};

export default ApiTokensPage;
